import { extractGradientColors } from "../../../../../general";

export function stringParseBuilder(matcher: RegExp | string[]) {
  const createEscapedRegex = (strings: string[]): RegExp => {
    const escapedStrings = strings.map((s) =>
      s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    return new RegExp(`(${escapedStrings.join("|")})`, "g");
  };

  const regex = Array.isArray(matcher) ? createEscapedRegex(matcher) : matcher;

  return (inputString: string) => {
    const matches: string[] = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(inputString)) !== null) {
      matches.push(match[1] || match[0]);
    }

    const parsedString = inputString
      .replace(regex, "")
      .replace(/\s+/g, " ")
      .trim();

    // Check if 'CLOSE_CHAT' is in the matched list
    const hasCloseChat = matches.includes("CLOSE_CHAT");
    return {
      originalString: inputString,
      matchedList: matches,
      parsedString,
      hasCloseChat, // Add this new property
    };
  };
}

export const SPECIAL_KEYS = { CLOSE_CHAT: "[CLOSE_CHAT]" };

export function wrapLinksInText(
  text: string,
  index: number,
  fontSize?: string
) {
  // const urlRegex = /(\bhttps?:\/\/[^\s]+)/gi;
  const urlRegex = /(\bhttps?:\/\/[^\s)]+)/gi;  // This stops at ')' in URLs

  const parts = text.split(urlRegex);

  const wrappedText = parts.map((part, index) => {
    if (urlRegex.test(part)) {
      const cleanUrl =
        part.endsWith(".") ||
        part.endsWith(")") ||
        part.endsWith("]") ||
        part.endsWith("}")
          ? part?.slice(0, -1)
          : part;
      return `<a
            key={${index}}
            href={${cleanUrl}}
            target="_blank"
            style={{ fontSize: ${fontSize} || "14px" }}
          >
            ${cleanUrl}
          </a>`;
    }
    return part;
  });

  return `<p key=${index}>${wrappedText}</p>`;
}

export function formatDate(dateString: string) {
  const options: { [key: string]: string } = {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString("en-US", options);
  return formattedDate;
}

export function formatMessage(message: string) {
  const messageParagraphs = message
    .split("\n")
    .map((line, index) => wrapLinksInText(line, index));
  return messageParagraphs;
}

export function checkForCloseChat(message: string) {
  const isCloseChat = message.includes("[CLOSE_CHAT]");
  const regularString = message.replace("[CLOSE_CHAT]", "");

  if (isCloseChat) {
    return {
      value: message,
      closeChatTag: "CLOSE_CHAT",
      isCloseChat: true,
      regularString: regularString,
    };
  } else {
    return {
      value: message,
      closeChatTag: "",
      isCloseChat: false,
      regularString: regularString,
    };
  }
}


export function adjustBrightness(hexColor, percent) {
  // Convert hex to RGB
  let r = parseInt(hexColor?.slice(1, 3), 16);
  let g = parseInt(hexColor?.slice(3, 5), 16);
  let b = parseInt(hexColor?.slice(5, 7), 16);

  // Adjust brightness
  r = Math.min(255, Math.max(0, Math.floor(r * (1 + percent))));
  g = Math.min(255, Math.max(0, Math.floor(g * (1 + percent))));
  b = Math.min(255, Math.max(0, Math.floor(b * (1 + percent))));

  // Convert RGB back to hex
  const newColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      ?.slice(1)
      .toUpperCase()}`;

  return newColor;
}

export function getColorVariations(hexColor,value) {
  const lighter = adjustBrightness(hexColor,value || 0.2); // 20% lighter
  const darker = adjustBrightness(hexColor, value || -0.5); // 20% darker
  return { lighter, darker };
}

export const hexToRgb = (hex) => {
  const bigint = parseInt(hex?.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `${r}, ${g}, ${b}`;
};


export function hexToGlassColor(color, opacity = 0.05) {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  if (color.includes('gradient')) {
    color = extractGradientColors(color);
  }

  if (color.startsWith('rgb')) {
    return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }

  if (!color.startsWith('#') && !color.startsWith('rgb')) {
    const tempEl = document.createElement('div');
    tempEl.style.color = color;
    document.body.appendChild(tempEl);

    const computedColor = getComputedStyle(tempEl).color;
    document.body.removeChild(tempEl);

    if (computedColor.startsWith('rgb')) {
      return computedColor.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }

    return `rgba(0, 0, 0, ${opacity})`;
  }

  let hex = color.replace('#', '');

  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  const r = parseInt(hex?.substring(0, 2), 16);
  const g = parseInt(hex?.substring(2, 4), 16);
  const b = parseInt(hex?.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}


